import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';

const IconArrow = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const products = [
  {
    category: 'Loans',
    items: [
      { id: 1, title: 'Biashara Loans' },
      { id: 2, title: 'Asset Financing' },
      { id: 3, title: 'Top-Up Loans' },
      { id: 14, title: 'Personal Loans' },
      { id: 15, title: 'LPO Financing' },
    ],
  },
  {
    category: 'Trade Finance',
    items: [
      { id: 5, title: 'Unsecured Bid Bonds' },
      { id: 6, title: 'Performance Bonds' },
      { id: 7, title: 'Advance Payment Guarantees' },
      { id: 8, title: 'Bank Guarantees' },
      { id: 16, title: 'Custom Bonds' },
      { id: 17, title: 'Retention Bonds' },
    ],
  },
  {
    category: 'Insurance Services',
    items: [
      { id: 9, title: 'Contractor’s All Risk (CAR)' },
      { id: 10, title: 'Work Injury Benefits Act (WIBA)' },
      { id: 11, title: 'Professional Indemnity' },
      { id: 12, title: 'Motor Insurance' },
      { id: 18, title: 'Medical Insurance' },
      { id: 19, title: 'Domestic Package' },
      { id: 20, title: 'Marine Insurance' },
      { id: 21, title: 'Travel Insurance' },
      { id: 22, title: "Golfer's Insurance" },
    ],
  },
];

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    category: '',
    product: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  const modalRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavLinkClick = (to) => {
    setIsMenuOpen(false);
    navigate(to);
  };

  const openApplyModal = () => {
    setIsApplyModalOpen(true);
    setFormData({ fullName: '', email: '', phone: '', message: '', category: '', product: '' });
    setFormErrors({});
    setSubmissionError('');
    setShowSuccessMessage(false);
  };

  const closeApplyModal = () => {
    setIsApplyModalOpen(false);
    setFormData({ fullName: '', email: '', phone: '', message: '', category: '', product: '' });
    setFormErrors({});
    setSubmissionError('');
    setShowSuccessMessage(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Reset product when category changes
      ...(name === 'category' ? { product: '' } : {}),
    }));
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
    if (submissionError) {
      setSubmissionError('');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName.trim() || formData.fullName.length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{9,}$/.test(formData.phone)) {
      errors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    if (!formData.product.trim()) {
      errors.product = 'Product is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('product', formData.product);

      const response = await axios.post('https://sokoxtra.com/api/apply-today-email.php', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Inquiry sent successfully:', response.data);
      setShowSuccessMessage(true);
      setFormErrors({});
      setTimeout(() => {
        closeApplyModal();
      }, 3000);
    } catch (error) {
      console.error('Error sending inquiry:', error);
      if (error.response) {
        setSubmissionError(`Failed to send inquiry: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        setSubmissionError('Failed to send inquiry. Please check your connection.');
      } else {
        setSubmissionError('Failed to send inquiry. An unexpected error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Focus trap for modal
  useEffect(() => {
    if (isApplyModalOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        } else if (e.key === 'Escape') {
          closeApplyModal();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      if (firstElement) firstElement.focus();

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isApplyModalOpen]);

  // Get available products based on selected category
  const availableProducts = formData.category
    ? products.find((cat) => cat.category === formData.category)?.items || []
    : [];

  return (
    <>
      <nav className="bg-white shadow-md fixed w-full z-20 border-b border-gray-200 animate-nav-in top-0 min-h-[48px]">
        <div className="container mx-auto px-4 sm:px-6 py-2 flex justify-between items-center">
          <div className="bg-white rounded-md px-2 py-1">
            <NavLink to="/" className="flex items-center group">
              <img
                src="/assets/images/logo.png"
                alt="TGL Credit Limited"
                className="w-[160px] h-[52px] sm:w-[180px] sm:h-[58px] max-w-full object-contain transition-transform duration-200 group-hover:scale-105"
                onError={(e) => {
                  console.error('Failed to load logo image: /assets/images/logo.jpg');
                  e.target.src = '/assets/images/fallback-logo.png';
                }}
              />
            </NavLink>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-[--maasai-blue] hover:text-[--maasai-red] focus:outline-none focus:ring-2 focus:ring-[--maasai-red] focus:ring-offset-2 transition-colors duration-200 p-2"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
          <div
            className={`md:flex md:space-x-4 md:items-center font-inter text-base font-medium text-[--maasai-blue] ${
              isMenuOpen ? 'block space-y-3 bg-white shadow-xl animate-slide-in-top overflow-y-auto max-h-[calc(100vh-48px)]' : 'hidden'
            } md:block absolute md:static top-[48px] left-0 w-full md:w-auto md:bg-transparent px-6 py-6 md:p-0 transition-all duration-300 ease-in-out z-30`}
          >
            <NavLink
              to="/"
              onClick={() => handleNavLinkClick('/')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              Home
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <NavLink
              to="/about-us"
              onClick={() => handleNavLinkClick('/about-us')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              About Us
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <NavLink
              to="/products"
              onClick={() => handleNavLinkClick('/products')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              Our Products
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <NavLink
              to="/careers"
              onClick={() => handleNavLinkClick('/careers')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              Careers
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <NavLink
              to="/branches"
              onClick={() => handleNavLinkClick('/branches')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              Branches
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <NavLink
              to="/contact-us"
              onClick={() => handleNavLinkClick('/contact-us')}
              className={({ isActive }) =>
                `nav-link block md:inline-block px-3 py-1.5 rounded-md transition-all duration-200 relative group transform hover:scale-105 hover:bg-[--maasai-blue]/10 ${
                  isActive ? 'text-white bg-[--maasai-red]' : 'text-[--maasai-blue] hover:text-[--maasai-blue]'
                } md:text-sm text-base tracking-tight`
              }
            >
              Contact Us
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[--maasai-red] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
            </NavLink>
            <button
              onClick={openApplyModal}
              className="nav-link block md:inline-flex bg-[--maasai-blue] text-white px-4 py-1.5 rounded-full hover:bg-[--maasai-red] transition-all duration-200 text-center items-center justify-center font-medium hover:shadow-lg hover:shadow-[--maasai-red]/30 z based tracking-tight"
              aria-label="Open Apply Today modal"
            >
              Apply Today
              <span className="ml-1.5 w-3 h-3 bg-white rounded-full flex items-center justify-center">
                <IconArrow className="w-2 h-2 text-[--maasai-blue] group-hover:translate-x-1 transition-transform duration-200" />
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* Apply Today Modal */}
      {isApplyModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-slide-in-left" role="dialog" aria-modal="true">
          <div
            ref={modalRef}
            className="bg-white/95 backdrop-blur-md rounded-lg w-full max-w-md sm:w-96 absolute right-0 top-0 h-full sm:h-auto sm:max-h-[calc(100vh-2rem)] overflow-y-auto p-8 sm:p-6 shadow-2xl border border-gray-200"
            style={{ boxShadow: '0 4px 12px rgba(47, 73, 140, 0.3), 0 2px 8px rgba(132, 51, 69, 0.2)' }}
          >
            <div className="flex flex-col">
              <div className="relative mb-6">
                <h3 className="text-lg sm:text-xl font-inter font-bold text-[--maasai-blue] tracking-tight text-center">
                  Apply Today
                </h3>
                <hr className="mt-2 mb-4 border-t-2 border-[--maasai-blue] w-1/2 mx-auto" />
                <button
                  onClick={closeApplyModal}
                  className="absolute top-0 right-0 text-[--maasai-blue] hover:text-[--maasai-red] focus:ring-2 focus:ring-[--maasai-red] focus:ring-offset-2 rounded-full"
                  aria-label="Close apply today modal"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {showSuccessMessage ? (
                <div className="bg-green-100 text-green-800 p-3 sm:p-4 rounded-lg mb-4 animate-fade-in" role="alert" aria-live="polite">
                  <h4 className="text-sm sm:text-base font-inter font-semibold text-green-800">Inquiry Sent Successfully!</h4>
                  <p className="text-xs sm:text-sm font-inter text-green-700">Thank you for your inquiry. We’ll respond within 2 hours.</p>
                </div>
              ) : (
                <>
                  {submissionError && (
                    <div className="bg-red-100 text-red-800 p-2 sm:p-3 rounded-lg mb-4 animate-fade-in" role="alert" aria-live="polite">
                      <p className="text-xs sm:text-sm">{submissionError}</p>
                    </div>
                  )}
                  {!submissionError && (
                    <p className="text-xs sm:text-sm font-inter font-medium text-gray-600 mb-4 sm:mb-6 text-center">
                      Our team will respond within 2 hours.
                    </p>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-4" noValidate>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="fullName" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                            formErrors.fullName ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                          } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none`}
                          placeholder="Enter full name"
                          required
                          aria-required="true"
                          aria-describedby={formErrors.fullName ? 'fullName-error' : undefined}
                        />
                        {formErrors.fullName && (
                          <p id="fullName-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                            {formErrors.fullName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="email" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                            formErrors.email ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                          } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none`}
                          placeholder="Enter email address"
                          required
                          aria-required="true"
                          aria-describedby={formErrors.email ? 'email-error' : undefined}
                        />
                        {formErrors.email && (
                          <p id="email-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                            formErrors.phone ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                          } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none`}
                          placeholder="Enter phone number"
                          required
                          aria-required="true"
                          aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                        />
                        {formErrors.phone && (
                          <p id="phone-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                            {formErrors.phone}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="category" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                          Category
                        </label>
                        <select
                          id="category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                            formErrors.category ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                          } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none`}
                          required
                          aria-required="true"
                          aria-describedby={formErrors.category ? 'category-error' : undefined}
                        >
                          <option value="">Select a category</option>
                          {products.map((cat) => (
                            <option key={cat.category} value={cat.category}>
                              {cat.category}
                            </option>
                          ))}
                        </select>
                        {formErrors.category && (
                          <p id="category-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                            {formErrors.category}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="product" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                        Product
                      </label>
                      <select
                        id="product"
                        name="product"
                        value={formData.product}
                        onChange={handleInputChange}
                        className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                          formErrors.product ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                        } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none`}
                        required
                        aria-required="true"
                        aria-describedby={formErrors.product ? 'product-error' : undefined}
                        disabled={!formData.category}
                      >
                        <option value="">Select a product</option>
                        {availableProducts.map((item) => (
                          <option key={item.id} value={item.title}>
                            {item.title}
                          </option>
                        ))}
                      </select>
                      {formErrors.product && (
                        <p id="product-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                          {formErrors.product}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="message" className="text-xs sm:text-sm font-inter font-semibold text-[--maasai-blue]">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className={`mt-1 w-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-inter text-gray-600 border ${
                          formErrors.message ? 'border-[--maasai-red]' : 'border-[--maasai-blue]/30'
                        } rounded-md focus:ring-2 focus:ring-[--maasai-blue] focus:outline-none resize-y min-h-[80px] sm:min-h-[100px]`}
                        placeholder="Tell us about your inquiry"
                        required
                        aria-required="true"
                        aria-describedby={formErrors.message ? 'message-error' : undefined}
                      />
                      {formErrors.message && (
                        <p id="message-error" className="text-[10px] sm:text-xs text-[--maasai-red] mt-1" role="alert">
                          {formErrors.message}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2 mt-4 sm:mt-6">
                      <button
                        type="button"
                        onClick={closeApplyModal}
                        className="bg-gray-200 text-[--maasai-blue] font-inter font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-gray-300 hover:text-[--maasai-red] transition-all duration-300 uppercase tracking-wide text-xs sm:text-sm focus:ring-2 focus:ring-[--maasai-blue] focus:ring-offset-2"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="group bg-[--maasai-blue] text-white font-inter font-medium px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-[--maasai-red] hover:shadow-lg hover:shadow-[--maasai-red]/30 transition-all duration-300 uppercase tracking-wide text-xs sm:text-sm focus:ring-2 focus:ring-[--maasai-blue] focus:ring-offset-2 flex items-center gap-2"
                        disabled={isSubmitting}
                        aria-label="Submit inquiry"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit
                            <span className="ml-2 rounded-full bg-white border border-[--maasai-blue] group-hover:border-[--maasai-red] p-1 flex items-center justify-center">
                              <IconArrow className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300 text-[--maasai-blue] group-hover:text-[--maasai-blue]" />
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          :root {
            --maasai-blue: #2F498C;
            --maasai-red: #843345;
          }
          nav .nav-link {
            font-size: 1rem; /* 16px for mobile */
          }
          @media (min-width: 768px) {
            nav .nav-link {
              font-size: 0.875rem; /* 14px for desktop, matches text-sm */
            }
          }
          @media (min-width: 776px) and (max-width: 1032px) {
            .container {
              max-width: 720px; /* Tight container for 776px, scales to 960px at 1032px */
            }
            nav .nav-link {
              font-size: 0.8125rem; /* 13px for better fit */
              padding: 0.375rem 0.5rem; /* Tighter padding */
            }
            .nav-link img {
              width: 170px; /* Adjusted for larger logo */
              height: 54px;
            }
            .md\\:space-x-4 {
              margin-right: 0.25rem; /* Tighten spacing */
            }
            .nav-link.button {
              padding: 0.375rem 0.75rem; /* Compact Apply Today button */
            }
          }
          @media (min-width: 1000px) and (max-width: 1032px) {
            .container {
              max-width: 960px; /* Slightly wider at 1032px */
            }
            .nav-link img {
              width: 190px; /* Adjusted for larger logo */
              height: 62px;
            }
          }
          @keyframes nav-in {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-nav-in {
            animation: nav-in 0.6s ease-out forwards;
          }
          @keyframes slide-in-top {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          .animate-slide-in-top {
            animation: slide-in-top 0.5s ease-out forwards;
          }
          @keyframes slide-in-left {
            from {
              transform: translateX(-100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .animate-slide-in-left {
            animation: slide-in-left 0.3s ease-out forwards;
          }
          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .animate-fade-in {
            animation: fade-in 0.8s ease-in-out forwards;
          }
          .backdrop-blur-sm {
            backdrop-filter: blur(4px);
            -webkit-backdrop-filter: blur(4px);
          }
          .backdrop-blur-md {
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }
          .bg-[--maasai-blue] {
            background-color: var(--maasai-blue);
          }
          .bg-[--maasai-red] {
            background-color: var(--maasai-red);
          }
          .text-[--maasai-blue] {
            color: var(--maasai-blue);
          }
          .text-[--maasai-red] {
            color: var(--maasai-red);
          }
          .border-[--maasai-blue]\/30 {
            border-color: rgba(47, 73, 140, 0.3);
          }
          .border-[--maasai-red] {
            border-color: var(--maasai-red);
          }
          .focus\\:ring-[--maasai-blue]:focus {
            --tw-ring-color: var(--maasai-blue);
          }
          .focus\\:ring-[--maasai-red]:focus {
            --tw-ring-color: var(--maasai-red);
          }
          .hover\\:bg-[--maasai-red]:hover {
            background-color: var(--maasai-red);
          }
          .hover\\:text-[--maasai-red]:hover {
            color: var(--maasai-red);
          }
          .hover\\:shadow-[--maasai-red]\/30:hover {
            --tw-shadow-color: rgba(132, 51, 69, 0.3);
            --tw-shadow: var(--tw-shadow-colored);
          }
          .group-hover\\:border-[--maasai-red]:hover {
            border-color: var(--maasai-red);
          }
          @media (max-width: 767px) {
            .container {
              padding-left: 1rem;
              padding-right: 1rem;
            }
            .nav-link {
              padding: 0.5rem 1rem; /* Adjusted padding for mobile */
              font-size: 0.9375rem; /* 15px for better readability */
            }
          }
          @media (max-width: 639px) {
            .max-w-md {
              max-width: none;
              width: 100%;
            }
            .sm\\:w-96 {
              width: 100%;
            }
            .sm\\:p-6 {
              padding: 1rem;
            }
            .sm\\:h-auto {
              height: 100%;
            }
            .sm\\:max-h-\\[calc\\(100vh-2rem\\)\\] {
              max-height: calc(100vh - 1rem);
            }
            .sm\\:text-sm {
              font-size: 0.75rem; /* 12px for mobile */
            }
            .sm\\:text-base {
              font-size: 0.875rem; /* 14px for mobile */
            }
            .sm\\:p-4 {
              padding: 0.75rem;
            }
            .sm\\:p-3 {
              padding: 0.5rem;
            }
            .sm\\:mt-6 {
              margin-top: 1rem;
            }
            .sm\\:mb-6 {
              margin-bottom: 1rem;
            }
            .sm\\:px-4 {
              padding-left: 0.75rem;
              padding-right: 0.75rem;
            }
            .sm\\:py-2 {
              padding-top: 0.5rem;
              padding-bottom: 0.5rem;
            }
            .sm\\:min-h-\\[100px\\] {
              min-height: 80px;
            }
            .sm\\:w-6 {
              width: 1.25rem;
            }
            .sm\\:h-6 {
              height: 1.25rem;
            }
            .sm\\:space-y-4 {
              gap: 0.75rem;
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .animate-nav-in, .animate-slide-in-top, .animate-slide-in-left, .animate-fade-in, .group-hover\\:scale-105, .group-hover\\:translate-x-1, .hover\\:scale-105, .hover\\:shadow-lg {
              animation: none;
              transform: none;
              opacity: 1;
              transition: none;
            }
          }
        `}
      </style>
    </>
  );
}

export default Navbar;